import { Box, Divider, Typography } from '@mui/material';
import { Pill, type PillTone } from '@/shared/ui/status/Pill';
import { formatReleaseNoteDate } from './formatReleaseNoteDate';
import type { ReleaseItemType, ReleaseNote } from '@/config/releaseNotes';

/**
 * Closed, hand-authored union — no cast, no fallback branch. If a 4th
 * `ReleaseItemType` is ever added to `releaseNotes.ts` without a matching
 * entry here, this object literal fails to compile (missing property),
 * which is the exhaustiveness guarantee we want (Swarm review, WNB-4:
 * TypeScript Critic explicitly rejected copying `RelationshipChip.tsx`'s
 * open-ended cast-and-fallback shape — that pattern exists for an
 * API-sourced, unbounded value; this union is neither). Lives here, not in
 * `WhatsNewPopoverContent.tsx`, so WNB-5's `/updates` page gets the exact
 * same exhaustiveness guarantee rather than a second, driftable copy
 * (Swarm review, WNB-5).
 *
 * `fix` is `primary` (blue), not `warning` (orange) — a 2026-10 polish pass
 * moved it off the "something's wrong" amber associated with warnings
 * elsewhere in the app, onto a neutral-but-distinct blue. Kept apart from
 * `new`'s `info` tone (teal — see `theme/index.ts`'s own comment: "distinct
 * from primary blue") so the three tags stay visually distinguishable at a
 * glance.
 */
const RELEASE_TAG: Record<ReleaseItemType, { readonly label: string; readonly tone: PillTone }> = {
  new: { label: 'NEW', tone: 'info' },
  improved: { label: 'IMPROVED', tone: 'success' },
  fix: { label: 'FIX', tone: 'primary' },
};

// Wide enough for the longest tag label ("IMPROVED") so every entry's title
// and description start at the same x position regardless of which tag it
// carries (2026-10 polish — the Pill's own natural width used to vary).
const TAG_COLUMN_WIDTH = 88;

export interface ReleaseNotesListProps {
  readonly releases: readonly ReleaseNote[];
}

/**
 * Pure, grouped-by-version/tagged rendering — no scroll cap, no footer
 * link. Those are both popover-only concerns (`WhatsNewPopoverContent`
 * composes this plus its own scroll wrapper and sticky footer); the
 * `/updates` page composes this directly with neither (Swarm arbitration,
 * WNB-5: adopted over a `showFooterLink` boolean on the shared component —
 * that would have made `maxHeight` and `showFooterLink` two independently
 * -settable but actually-coupled props with no compiler-enforced pairing).
 */
export const ReleaseNotesList = ({ releases }: ReleaseNotesListProps): JSX.Element => {
  if (releases.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No updates yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {releases.map((release, releaseIndex) => {
        const isLatest = releaseIndex === 0;
        return (
          <Box key={release.version} sx={{ px: 2, py: 2 }}>
            {releaseIndex > 0 ? <Divider sx={{ mb: 2 }} /> : null}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Pill tone="primary" filled={false} sx={{ textTransform: 'none' }}>
                v{release.version}
              </Pill>
              {isLatest ? <Pill tone="primary">Latest</Pill> : null}
              <Typography variant="caption" color="text.secondary">
                {formatReleaseNoteDate(release.date)}
              </Typography>
            </Box>
            {release.items.map((item, index) => {
              const tag = RELEASE_TAG[item.type];
              const title = item.title.replace(/\.$/, '');
              return (
                <Box
                  key={`${release.version}-${index}`}
                  sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}
                >
                  <Box sx={{ width: TAG_COLUMN_WIDTH, flexShrink: 0, mt: 0.25 }}>
                    <Pill tone={tag.tone}>{tag.label}</Pill>
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {title}
                      </Typography>
                      <Pill tone="neutral" filled={false} sx={{ fontSize: '0.65rem', py: 0, flexShrink: 0 }}>
                        {item.area}
                      </Pill>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};
